import React from "react";
import { Box, alpha, keyframes } from "@mui/material";
import { COLORS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";

// Tek SVG, CSS animasyonları (transform-box ile her parça kendi merkezinde döner).
const roam = keyframes`
  0%   { transform: translate(-44px, -6px) rotate(-10deg); }
  25%  { transform: translate(-6px, -26px) rotate(4deg); }
  50%  { transform: translate(40px, -4px) rotate(12deg); }
  75%  { transform: translate(4px, 18px) rotate(0deg); }
  100% { transform: translate(-44px, -6px) rotate(-10deg); }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;
const glint = keyframes`
  0%, 60% { transform: translateX(-26px); opacity: 0; }
  70% { opacity: 0.9; }
  100% { transform: translateX(26px); opacity: 0; }
`;
const twinkle = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.6); }
  50% { opacity: 0.9; transform: scale(1); }
`;

const DOCS = [
  { x: 58, y: 44, delay: 0 },
  { x: 182, y: 40, delay: 0.6 },
  { x: 120, y: 104, delay: 1.2 },
];
const SPARKS = [
  { x: 34, y: 92, delay: 0 },
  { x: 206, y: 96, delay: 0.8 },
  { x: 150, y: 20, delay: 1.6 },
  { x: 86, y: 18, delay: 2.2 },
];

/**
 * Hareketli arama illüstrasyonu (SVG) — boş liste/tablo ve yükleme katmanı ortak.
 * Merkezde bağlam kutucuğu (ikon HTML olarak üstüne biner), etrafında süzülen
 * belgeler, dönen kesikli tarama halkası, belgelerin üstünde gezen büyüteç ve
 * camında süzülen parlama. Hareket azaltma tercihinde tüm animasyonlar durur.
 *
 * @param {React.ReactNode} [icon] - merkezdeki kutucuğun ikonu
 * @param {"md"|"sm"} [size="md"]
 */
export default function SearchIllustration({ icon, size = "md" }) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate500;
  const s = size === "sm" ? 0.75 : 1;
  const anim = (a) => ({ animation: a, transformBox: "fill-box", transformOrigin: "center" });

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 240 * s,
        maxWidth: "100%",
        height: 132 * s,
        "@media (prefers-reduced-motion: reduce)": { "& *": { animation: "none !important" } },
      }}
    >
      <Box component="svg" viewBox="0 0 240 132" sx={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}>
        {/* Yumuşak hale */}
        <defs>
          <radialGradient id="si-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={alpha(tone, 0.16)} />
            <stop offset="100%" stopColor={alpha(tone, 0)} />
          </radialGradient>
        </defs>
        <circle cx="120" cy="66" r="72" fill="url(#si-halo)" />

        {/* Dönen kesikli tarama halkası */}
        <Box
          component="circle"
          cx="120"
          cy="66"
          r="46"
          fill="none"
          stroke={alpha(tone, 0.35)}
          strokeWidth="1.5"
          strokeDasharray="4 7"
          sx={anim(`${spin} 14s linear infinite`)}
        />

        {/* Süzülen belgeler */}
        {DOCS.map((d, i) => (
          <Box component="g" key={i} sx={anim(`${bob} ${4 + i}s ease-in-out ${d.delay}s infinite`)}>
            <rect x={d.x - 13} y={d.y - 16} width="26" height="32" rx="5" fill={COLORS.white} stroke={COLORS.slate200} />
            <line x1={d.x - 7} y1={d.y - 7} x2={d.x + 7} y2={d.y - 7} stroke={COLORS.slate300} strokeWidth="2" strokeLinecap="round" />
            <line x1={d.x - 7} y1={d.y - 1} x2={d.x + 4} y2={d.y - 1} stroke={COLORS.slate200} strokeWidth="2" strokeLinecap="round" />
            <line x1={d.x - 7} y1={d.y + 5} x2={d.x + 6} y2={d.y + 5} stroke={COLORS.slate200} strokeWidth="2" strokeLinecap="round" />
          </Box>
        ))}

        {/* Parlayan noktalar */}
        {SPARKS.map((p, i) => (
          <Box
            component="circle"
            key={i}
            cx={p.x}
            cy={p.y}
            r="2.5"
            fill={tone}
            sx={anim(`${twinkle} 2.6s ease-in-out ${p.delay}s infinite`)}
          />
        ))}

        {/* Merkez kutucuk (ikon HTML olarak üstte) */}
        <rect x="94" y="40" width="52" height="52" rx="14" fill={COLORS.white} stroke={COLORS.slate200} />

        {/* Gezen büyüteç + cam parlaması */}
        <Box component="g" sx={{ ...anim(`${roam} 6s ease-in-out infinite`), transformOrigin: "120px 66px" }}>
          <defs>
            <clipPath id="si-lens">
              <circle cx="120" cy="62" r="16" />
            </clipPath>
          </defs>
          <circle cx="120" cy="62" r="19" fill={alpha(COLORS.white, 0.75)} stroke={tone} strokeWidth="4" />
          <g clipPath="url(#si-lens)">
            {/* Eğim dış grupta: CSS animasyonu rect'in transform özniteliğini ezerdi */}
            <g transform="rotate(25 120 62)">
              <Box
                component="rect"
                x="112"
                y="40"
                width="8"
                height="44"
                fill={COLORS.white}
                sx={{ animation: `${glint} 3s ease-in-out infinite` }}
              />
            </g>
          </g>
          <line x1="133" y1="76" x2="146" y2="90" stroke={tone} strokeWidth="6" strokeLinecap="round" />
        </Box>
      </Box>

      {/* Merkez ikon */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: COLORS.slate400,
          display: "flex",
          "& svg": { fontSize: 24 * s },
          pointerEvents: "none",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
}
